import styled from 'styled-components';
import {Switch, Route, Redirect} from 'react-router-dom';
import {TicketListRoute} from './features/tickets/routes/TicketListRoute';
import {TicketDetailRoute} from './features/tickets/routes/TicketDetailRoute';
import {RequireAdmin} from './features/admin/components/RequireAdmin';
import {SettingsRoute} from './features/admin/routes/SettingsRoute';
import {AnalyticsRoute} from './features/admin/routes/AnalyticsRoute';
import {DashboardRoute} from './features/dashboard/routes/DashboardRoute';
import {EmbeddedProvider} from './EmbeddedContext';
import {TicketingThemeProvider} from './styles/TicketingThemeProvider';

// ACC-523: the single outermost ticketing element. Tailwind has been removed, so
// this is no longer a style-scoping anchor — it just supplies the white surface.
// All ticketing DOM (incl. inline modals/drawers/toasts — ticketing uses no
// portals) is contained here.
const TicketingRoot = styled.div`
	background: #ffffff;
`;

// Ticketing only ever runs embedded in the Forecast shell (web-front
// ticketing-embed.tsx renders <App />); the Forecast shell supplies all
// top-level chrome, so ticketing renders no nav of its own. The former
// standalone path (its own AppNav/MobileDrawer + the standalone main.tsx entry)
// was removed once the embed became the only mount point.
export default function App() {
	return (
		<EmbeddedProvider embedded={true}>
			{/* The styled-components theme is supplied here so every component
          can read design-system tokens via theme.sys.*. */}
			<TicketingThemeProvider>
				{/* The Forecast shell owns viewport height and supplies a flex-fill scroll
            region (web-front ticketing-embed.tsx); forcing 100vh here would overflow
            that region and leave header-height of dead scroll on short tickets. */}
				<TicketingRoot>
					<Switch>
						{/* Embedded under Forecast's /tickets route (ACC-434). Forecast owns "/",
                so there is no root redirect here. Static segments precede "/tickets/:id"
                so ":id" never captures "new" / "settings". */}
						<Redirect exact from="/tickets/new" to="/tickets" />
						{/* FOR-229: the standalone ticket-preferences page was removed in favour of
                the native My Profile tab. Without this, "profile" falls through to
                "/tickets/:id" and stale bookmarks spend an authenticated ticketDetail
                lookup before rendering TicketNotFound. Ticketing shares Forecast's single
                router (ACC-434, no nested basename), so this redirect leaves the /tickets
                tree and lands on the real page. */}
						<Redirect exact from="/tickets/profile" to="/my-profile/ticket-preferences" />
						<Route path="/tickets" exact>
							<TicketListRoute />
						</Route>
						<Route path="/tickets/settings">
							<RequireAdmin>
								<SettingsRoute />
							</RequireAdmin>
						</Route>
						{/* ACC-298: internal-only AI summary feedback analytics. Admin-gated;
                placed before ":id" so "analytics" is never captured as a ticket id. */}
						<Route path="/tickets/analytics">
							<RequireAdmin>
								<AnalyticsRoute />
							</RequireAdmin>
						</Route>
						<Route path="/tickets/dashboard">
							<RequireAdmin>
								<DashboardRoute />
							</RequireAdmin>
						</Route>
						<Route path="/tickets/:id">
							<TicketDetailRoute />
						</Route>
					</Switch>
				</TicketingRoot>
			</TicketingThemeProvider>
		</EmbeddedProvider>
	);
}
